import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { transactionAPI, entityAPI } from '../lib/api';
import { formatDate, formatRelativeTime, formatCurrency, getRiskClass } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  ArrowLeft,
  ArrowRight,
  ArrowLeftRight,
  Banknote,
  Bitcoin,
  CreditCard,
  Smartphone,
  AlertTriangle,
  Shield,
  RefreshCw,
  FileText,
  User,
  Building,
  Clock,
  Globe,
  ExternalLink,
  Eye,
  Filter,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const TRANSACTION_TYPES = [
  { value: 'wire', label: 'Wire Transfer', icon: ArrowLeftRight },
  { value: 'cash', label: 'Cash', icon: Banknote },
  { value: 'crypto', label: 'Crypto', icon: Bitcoin },
  { value: 'mobile_network', label: 'Mobile Network', icon: Smartphone },
  { value: 'card', label: 'Card', icon: CreditCard },
  { value: 'trade', label: 'Trade', icon: Globe },
  { value: 'real_estate', label: 'Real Estate', icon: Building },
];

// Transaction List Component
function TransactionList() {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    type: '',
    flagged: '',
    minAmount: '',
    maxAmount: '',
  });
  const [page, setPage] = useState(0);
  const limit = 20;

  useEffect(() => {
    fetchTransactions();
  }, [page, filters.type, filters.flagged]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = {
        skip: page * limit,
        limit,
        ...(filters.type && { transaction_type: filters.type }),
        ...(filters.flagged && { is_flagged: filters.flagged === 'true' }),
        ...(filters.minAmount && { min_amount: parseFloat(filters.minAmount) }),
        ...(filters.maxAmount && { max_amount: parseFloat(filters.maxAmount) }),
      };
      const response = await transactionAPI.list(params);
      setTransactions(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    const t = TRANSACTION_TYPES.find(x => x.value === type);
    return t ? t.icon : ArrowLeftRight;
  };

  if (loading && transactions.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="transactions-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-secondary text-2xl font-bold uppercase tracking-wide text-white">
            Transaction Monitoring
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {total.toLocaleString()} transactions analyzed
          </p>
        </div>
        <Button variant="outline" onClick={fetchTransactions} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="glass">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <Select value={filters.type || "all"} onValueChange={(v) => setFilters({...filters, type: v === "all" ? "" : v})}>
              <SelectTrigger className="w-[160px] bg-black/20 border-white/10">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all">All Types</SelectItem>
                {TRANSACTION_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.flagged || "all"} onValueChange={(v) => setFilters({...filters, flagged: v === "all" ? "" : v})}>
              <SelectTrigger className="w-[160px] bg-black/20 border-white/10">
                <AlertTriangle className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="true">Flagged</SelectItem>
                <SelectItem value="false">Normal</SelectItem>
              </SelectContent>
            </Select>

            <Input
              type="number"
              placeholder="Min Amount"
              value={filters.minAmount}
              onChange={(e) => setFilters({...filters, minAmount: e.target.value})}
              className="w-[140px] bg-black/20 border-white/10"
            />

            <Input
              type="number"
              placeholder="Max Amount"
              value={filters.maxAmount}
              onChange={(e) => setFilters({...filters, maxAmount: e.target.value})}
              className="w-[140px] bg-black/20 border-white/10"
            />

            <Button variant="secondary" onClick={fetchTransactions}>Apply</Button>
            <Button variant="ghost" onClick={() => setFilters({ type: '', flagged: '', minAmount: '', maxAmount: '' })}>
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card className="glass">
        <CardContent className="p-0">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Reference</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Sender</TableHead>
                <TableHead></TableHead>
                <TableHead>Receiver</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Risk</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.map((txn) => {
                const TypeIcon = getTypeIcon(txn.transaction_type);
                return (
                  <TableRow 
                    key={txn.id} 
                    className={`cursor-pointer hover:bg-white/5 ${txn.is_flagged ? 'bg-red-500/5' : ''}`}
                    onClick={() => navigate(`/transactions/${txn.id}`)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {txn.is_flagged && <AlertTriangle className="w-4 h-4 text-red-400" />}
                        <span className="font-mono text-xs">{txn.transaction_ref?.slice(0, 15)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <TypeIcon className="w-4 h-4 text-muted-foreground" />
                        <Badge variant="outline" className="text-xs uppercase">{txn.transaction_type}</Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-white">{txn.sender_name?.slice(0, 20)}</TableCell>
                    <TableCell><ArrowRight className="w-4 h-4 text-primary" /></TableCell>
                    <TableCell className="text-sm text-white">{txn.receiver_name?.slice(0, 20)}</TableCell>
                    <TableCell className="text-right font-mono text-white">
                      {formatCurrency(txn.amount, txn.currency)}
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-xs">{txn.risk_score || 0}</span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatRelativeTime(txn.timestamp)}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); navigate(`/transactions/${txn.id}`); }}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={(page + 1) * limit >= total} onClick={() => setPage(page + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Transaction Detail Component
function TransactionDetail({ transactionId }) {
  const navigate = useNavigate();
  const [transaction, setTransaction] = useState(null);
  const [senderEntity, setSenderEntity] = useState(null);
  const [receiverEntity, setReceiverEntity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTransactionDetails();
  }, [transactionId]);

  const fetchTransactionDetails = async () => {
    setLoading(true);
    try {
      const txnRes = await transactionAPI.get(transactionId);
      setTransaction(txnRes.data);

      // Try to fetch sender and receiver entities
      if (txnRes.data.sender_id) {
        try {
          const senderRes = await entityAPI.get(txnRes.data.sender_id);
          setSenderEntity(senderRes.data);
        } catch (e) { /* Entity might not exist */ }
      }
      if (txnRes.data.receiver_id) {
        try {
          const receiverRes = await entityAPI.get(txnRes.data.receiver_id);
          setReceiverEntity(receiverRes.data);
        } catch (e) { /* Entity might not exist */ }
      }
    } catch (error) {
      toast.error('Failed to load transaction details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Transaction not found</p>
        <Button variant="outline" onClick={() => navigate('/transactions')} className="mt-4">
          Back to Transactions
        </Button>
      </div>
    );
  }

  const TypeIcon = TRANSACTION_TYPES.find(t => t.value === transaction.transaction_type)?.icon || ArrowLeftRight;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="transaction-detail">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/transactions')} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-4 mb-2">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${transaction.is_flagged ? 'bg-red-500/20' : 'bg-primary/20'}`}>
              <TypeIcon className={`w-6 h-6 ${transaction.is_flagged ? 'text-red-400' : 'text-primary'}`} />
            </div>
            <div>
              <h1 className="font-secondary text-xl font-bold text-white font-mono">
                {transaction.transaction_ref}
              </h1>
              <div className="flex items-center gap-3 mt-1">
                <Badge variant="outline" className="text-xs uppercase">
                  {transaction.transaction_type}
                </Badge>
                {transaction.is_flagged && (
                  <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    FLAGGED
                  </Badge>
                )}
                <Badge variant="outline" className="text-xs">{transaction.status}</Badge>
              </div>
            </div>
          </div>
        </div>
        <Button className="gap-2" data-testid="create-alert-btn">
          <FileText className="w-4 h-4" />
          Create Alert
        </Button>
      </div>

      {/* Amount Display */}
      <Card className="glass">
        <CardContent className="p-6 text-center">
          <p className="text-4xl font-bold text-white font-mono">
            {formatCurrency(transaction.amount, transaction.currency)}
          </p>
          <p className="text-muted-foreground mt-2">Transaction Amount</p>
        </CardContent>
      </Card>

      {/* Transfer Flow */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
        {/* Sender */}
        <Card className="glass">
          <CardHeader className="border-b border-white/5 pb-3">
            <CardTitle className="font-secondary text-sm uppercase tracking-wider text-muted-foreground">
              Sender
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <User className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-white font-medium">{transaction.sender_name}</p>
                {senderEntity && (
                  <Badge variant="outline" className={`${getRiskClass(senderEntity.risk_level)} text-[10px]`}>
                    {senderEntity.risk_level} risk
                  </Badge>
                )}
              </div>
            </div>
            {transaction.sender_id && (
              <Link to={`/entities/${transaction.sender_id}`}>
                <Button variant="outline" size="sm" className="w-full gap-2">
                  <ExternalLink className="w-3 h-3" />
                  View Entity
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>

        {/* Arrow */}
        <div className="flex justify-center">
          <div className="p-4 rounded-full bg-primary/20">
            <ArrowRight className="w-8 h-8 text-primary" />
          </div>
        </div>

        {/* Receiver */}
        <Card className="glass">
          <CardHeader className="border-b border-white/5 pb-3">
            <CardTitle className="font-secondary text-sm uppercase tracking-wider text-muted-foreground">
              Receiver
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <User className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-white font-medium">{transaction.receiver_name}</p>
                {receiverEntity && (
                  <Badge variant="outline" className={`${getRiskClass(receiverEntity.risk_level)} text-[10px]`}>
                    {receiverEntity.risk_level} risk
                  </Badge>
                )}
              </div>
            </div>
            {transaction.receiver_id && (
              <Link to={`/entities/${transaction.receiver_id}`}>
                <Button variant="outline" size="sm" className="w-full gap-2">
                  <ExternalLink className="w-3 h-3" />
                  View Entity
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Transaction Details */}
        <Card className="glass">
          <CardHeader className="border-b border-white/5 pb-4">
            <CardTitle className="font-secondary text-sm uppercase tracking-wider">
              Transaction Details
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="label-mono">Type</p>
                <p className="text-white capitalize">{transaction.transaction_type}</p>
              </div>
              <div>
                <p className="label-mono">Currency</p>
                <p className="text-white">{transaction.currency}</p>
              </div>
              <div>
                <p className="label-mono">Status</p>
                <p className="text-white capitalize">{transaction.status}</p>
              </div>
              <div>
                <p className="label-mono">Timestamp</p>
                <p className="text-white">{formatDate(transaction.timestamp)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Risk Analysis */}
        <Card className="glass">
          <CardHeader className="border-b border-white/5 pb-4">
            <CardTitle className="font-secondary text-sm uppercase tracking-wider flex items-center gap-2">
              <Shield className="w-4 h-4 text-primary" />
              Risk Analysis
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
              <span className="text-muted-foreground">Risk Score</span>
              <span className={`text-2xl font-bold ${transaction.risk_score >= 50 ? 'text-red-400' : transaction.risk_score >= 30 ? 'text-yellow-400' : 'text-green-400'}`}>
                {transaction.risk_score || 0}/100
              </span>
            </div>
            
            {transaction.risk_indicators?.length > 0 && (
              <div>
                <p className="label-mono mb-2">Risk Indicators</p>
                <div className="space-y-2">
                  {transaction.risk_indicators.map((indicator, i) => (
                    <div 
                      key={i}
                      className="flex items-center gap-2 p-2 rounded bg-red-500/10 border border-red-500/20"
                    >
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                      <span className="text-sm text-red-300 capitalize">
                        {indicator.replace(/_/g, ' ')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(!transaction.risk_indicators || transaction.risk_indicators.length === 0) && (
              <div className="text-center py-4">
                <Shield className="w-8 h-8 text-green-400 mx-auto mb-2" />
                <p className="text-muted-foreground">No risk indicators detected</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Main Component
export default function Transactions() {
  const { id } = useParams();
  
  if (id) {
    return <TransactionDetail transactionId={id} />;
  }
  
  return <TransactionList />;
}
